import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ContentType } from '../api/Content';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
    selectCurrentRecommendation,
    selectRecommendationsError,
    selectRecommendationStatus,
} from '../features/recommendations/logic/Slice';
import {
    createRecommendationsThunk,
    getRecommendationsThunk,
    updateRecommendationsThunk
} from '../features/recommendations/logic/Thunk';
import { createContent as createContentThunk } from '../thunks/Content';


export interface UseRecommendationsReturn {
  initialValue: string;
  isLoading: boolean;
  error: string | null;
  onSave: (newText: string) => Promise<void>;
  onImprove: (currentText: string) => Promise<string | null>;
  onGenerate: () => Promise<string | undefined>;
}


export const useRecommendations = (): UseRecommendationsReturn => {
  const dispatch = useAppDispatch();
  const { id: caseId } = useParams<{ id: string }>();

  const status = useAppSelector((state) => selectRecommendationStatus(state, caseId));
  const error = useAppSelector((state) => selectRecommendationsError(state, caseId));
  const recommendations = useAppSelector((state) => selectCurrentRecommendation(state, caseId));
  const initialValue = useMemo(() => recommendations?.recommendations?.value || '', [recommendations]);

  useEffect(() => {
    if (caseId && status !== 'loading' && status !== 'succeeded') {
      dispatch(getRecommendationsThunk({ case_id: caseId }));
    }
  }, [dispatch, caseId, status]);

  const onSave = useCallback(async (newText: string) => {
    if (!caseId) {
      throw new Error("Case ID not found.");
    }
    if (!recommendations) {
      throw new Error("Recommendations not loaded yet. Please try again.");
    }
    
    const currentFieldValue = recommendations.recommendations as any;
    const currentAiGenerated = currentFieldValue?.ai_generated ?? "false";
    const updateRequest = {
        recommendations: { value: newText, ai_generated: currentAiGenerated, user_updated: "true" }
    };
    
    try {
      await dispatch(updateRecommendationsThunk({ caseId, request: updateRequest })).unwrap();
    } catch (err) {
      console.error('Failed to update recommendations:', err);
      throw err;
    }
  }, [caseId, recommendations, dispatch]);

  const onGenerate = useCallback(async () => {
    if (!caseId) return;

    try {
      const payload = await dispatch(
        createRecommendationsThunk({ case_id: caseId })
      ).unwrap();
      return payload.recommendations?.value;
    } catch (err) {
      console.error('Failed to generate recommendations:', err);
      throw err;
    }
  }, [caseId, dispatch]);

  const onImprove = useCallback(async (currentText: string) => {
    try {
      const response = await dispatch(createContentThunk({
        type: ContentType.DEFAULT,
        existing_content: currentText,
        context: {}
      })).unwrap();
      return response.content;
    } catch (err) {
      console.error('Failed to improve recommendations:', err);
      throw err;
    }
  }, [dispatch]);

  return {
    initialValue,
    isLoading: status === 'loading',
    error: error ?? null,
    onSave,
    onGenerate,
    onImprove,
  };
};